/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../../core/components';
import { Component } from '@angular/core';
import { DashboardService } from '../../../../services/component';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetModel } from '../../../../models';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-widget',
    templateUrl: './widget.component.html',
    styleUrls: ['./widget.component.scss']
})
export abstract class WidgetComponent extends BaseComponent {
    /**
     * Data has loaded.
     */
    public loaded: boolean = false;

    /**
     * Data updating state, normally triggered by a change of namespace.
     */
    public dataUpdating: boolean = false;

    public showLoading: boolean = false;

    /**
     * Base view object to be used to loop and populate in each widget.
     */
    public baseView: WidgetModel = {
        type: '',
        flagState: '',
        iconSrc: '',
        title: '',
        subtitle: '',
        counters: [],
        message: '',
        messageLink: '',
        messageLinkText: '',
        list: [],
        mainImageSrc: '',
        linkUrl: '',
        linkText: '',
        hasSlider: false,
        sliderContent: {}
    };

    /**
     * Store the formatted data for the view.
     */
    public view: WidgetModel;

    /**
     * State of slide message.
     */
    public slideMessageOpen: boolean = false;

    /**
     * Store any errors
     */
    public errors: any = {};

    protected constructor(
        protected dashboardService: DashboardService,
        protected utilsService: UtilsService
    ) {
        super();

        this.errors = this.dashboardService.errors;

        this.dashboardService.loadingEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    // This is here because of the transition in the CSS.
                    if (state) {
                        this.dataUpdating = state;
                        const loadingTimer: any = setTimeout((): void => {
                            this.showLoading = state;
                            clearTimeout(loadingTimer);
                        }, 0);
                    } else {
                        this.showLoading = state;
                        const loadingTimer: any = setTimeout((): void => {
                            this.dataUpdating = state;
                            clearTimeout(loadingTimer);
                        }, 500);
                    }
                }
            });
    }

    /**
     * Slide message overlay.
     */
    public slideMessage(): void {
        if (this.view.hasSlider) {
            this.slideMessageOpen = !this.slideMessageOpen;
        }
    }

    /**
     * Update the view object.
     * @param baseObject
     * @param newObject
     */
    public updateObject(baseObject: WidgetModel, newObject: any): WidgetModel {
        const formattedViewData = JSON.parse(JSON.stringify(baseObject));
        for (const property in formattedViewData) {
            if (formattedViewData.hasOwnProperty(property)) {
                if (newObject.hasOwnProperty(property)) {
                    formattedViewData[property] = newObject[property];
                }
            }
        }

        return formattedViewData;
    }

    /**
     * Calculate the percentage of usage.
     * @param count
     * @param max
     * @param threshold
     */
    public percentageOfUsage(count: number, max: number, threshold: number = 80): boolean {
        const percentage = Math.round((count / max) * 100);

        return percentage > threshold;
    }

    /**
     * Get error messaging.
     * @param viewObject
     * @param error
     */
    public getErrorMessaging(viewObject: WidgetModel, error: any): WidgetModel {
        viewObject.flagState = 'hidden';
        viewObject.linkText = 'view_service_status';
        viewObject.linkUrl = 'https://status.loadbalancer.org/';
        viewObject.linkExternal = true;
        viewObject.error = {
            message: 'service_outage',
            dateTimeMessage: this.formatDate(error.date),
            err: error.err?.status
        };

        return viewObject;
    }

    /**
     * Format date
     * @param date
     * @private
     */
    private formatDate(date: string | number | Date): any {
        const now: Date = new Date();
        const apiCall: Date = new Date(date);
        if (apiCall.setHours(0, 0, 0, 0) === now.setHours(0, 0, 0, 0)) {
            return {
                isToday: true,
                timeString: this.utilsService.formatTimeForHtmlInput(date)
            };
        } else {
            return {
                isToday: false,
                timeString: this.utilsService.createDateTimeString(date)
            };
        }
    }
}
